/**
 * SA-1093: an email body shown as its HTML, in a sandboxed frame. The HTML
 * and each image it cites by Content-ID are fetched through apiClient (the media route is
 * Bearer-gated, so a bare src would 401), an image through the shared hold an
 * attachment row reads too, so a cited attachment is fetched once (SA-1122);
 * the document is sanitized and
 * composed by `emailHtml.ts` and placed in `srcdoc`, never navigated to.
 * `allow-same-origin` without `allow-scripts` is what lets the parent read
 * the frame's height; a document with no scripts cannot reach the parent.
 * A failed HTML read shows the text, as before this item.
 * SA-1309: a reply's quoted history folds behind the text view's toggle (`foldQuotedHtml`);
 * open is per message and not remembered: it resets when the message changes, and only then
 * (SA-1311), so a refetch for new inline rows keeps it.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { apiClient } from '@/services/apiClient'
import type { InboxMessage } from '@/types/inbox'
import { InboxEmailBody, QuotedToggle } from './InboxEmailBody'
import { acquireMediaBlob, type MediaBlobLease } from './mediaBlob'
import { SANDBOX, cidKey, citedCids, composeEmailDocument, foldQuotedHtml, sanitizeEmailHtml } from './emailHtml'
import { FRAME_TITLE, HTML_LOADING } from './emailHtmlText'

const MIN_HEIGHT = 48

interface Props {
  messageId: string
  cidRows: readonly InboxMessage[]
  externalImages: boolean
  fallbackText: string
}

type Loaded = { html: string; cidUrls: Map<string, string> }

export function InboxEmailHtml({ messageId, cidRows, externalImages, fallbackText }: Props) {
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [failed, setFailed] = useState(false)
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const [height, setHeight] = useState(MIN_HEIGHT)
  // SA-1309: the quoted history is folded until opened; per message, never remembered.
  const [open, setOpen] = useState(false)
  // SA-1311: reset on a message change only, during render, so new inline rows (which reload
  // the HTML) never close it.
  const [openFor, setOpenFor] = useState(messageId)
  if (openFor !== messageId) {
    setOpenFor(messageId)
    setOpen(false)
  }
  const frameId = useId()
  // The rows' identity changes on every refetch; their ids are what matters.
  const cidKeyIds = cidRows.map((r) => r.id).join(',')

  useEffect(() => {
    let cancelled = false
    const leases: MediaBlobLease[] = []
    async function load() {
      try {
        const res = await apiClient.fetch(`/api/media/${messageId}/html`)
        if (!res.ok) throw new Error(`status ${res.status}`)
        const html = await res.text()
        // SA-1110 (R3), SA-1122: only a Content-ID the rewrite looks up is
        // fetched, so an ordinary attachment that carries one is never pulled
        // for nothing, and an image's srcset is read only when its src is no
        // row this frame can fetch.
        const fetchable = new Map<string, string>()
        for (const row of cidRows) {
          const raw = row.channelMetadata?.content_id
          const key = typeof raw === 'string' ? cidKey(raw) : null
          if (key && row.mediaUrl && !fetchable.has(key)) fetchable.set(key, row.mediaUrl)
        }
        const cited = fetchable.size ? citedCids(html, fetchable.keys()) : new Set<string>()
        const cidUrls = new Map<string, string>()
        await Promise.all([...fetchable].map(async ([key, mediaUrl]) => {
          if (!cited.has(key) || cancelled) return
          const lease = acquireMediaBlob(mediaUrl)
          leases.push(lease)
          // A failed image stays a broken box (spec R7).
          const { objectUrl } = await lease.result
          if (objectUrl) cidUrls.set(key, objectUrl)
        }))
        if (cancelled) return
        setLoaded({ html, cidUrls })
      } catch {
        if (!cancelled) setFailed(true)
      }
    }
    setLoaded(null)
    setFailed(false)
    void load()
    return () => {
      cancelled = true
      for (const lease of leases) lease.release()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, cidKeyIds])

  // Sanitize once per load; the fold and the toggle recompose from these strings (SA-1309).
  const sanitized = useMemo(() => {
    if (!loaded) return null
    try {
      return sanitizeEmailHtml(loaded.html, { cidUrls: loaded.cidUrls })
    } catch (err) {
      console.warn('SA-1093: the email HTML could not be sanitized', messageId, err)
      return null
    }
  }, [loaded, messageId])
  const folded = useMemo(() => (sanitized ? foldQuotedHtml(sanitized) : null), [sanitized])

  const doc = useMemo(() => {
    if (sanitized === null) return null
    try {
      return composeEmailDocument(open || folded === null ? sanitized : folded, { externalImages })
    } catch (err) {
      console.warn('SA-1093: the email HTML could not be composed', messageId, err)
      return null
    }
  }, [sanitized, folded, open, externalImages, messageId])

  useEffect(() => {
    const frame = frameRef.current
    if (!frame || !doc) return
    let observer: ResizeObserver | null = null
    const measure = () => {
      const root = frame.contentDocument?.documentElement
      if (!root) return
      // A standards-mode root reports at least the frame's own height, so a document that got
      // shorter (the quote hidden again, SA-1309) would never shrink it. Measure at the minimum,
      // then put the height straight back: one task, so nothing paints in between.
      frame.style.height = `${MIN_HEIGHT}px`
      const next = Math.max(MIN_HEIGHT, root.scrollHeight)
      frame.style.height = `${next}px`
      setHeight(next)
    }
    const onLoad = () => {
      measure()
      const target = frame.contentDocument?.body
      if (target && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure)
        observer.observe(target)
      }
    }
    frame.addEventListener('load', onLoad)
    return () => {
      frame.removeEventListener('load', onLoad)
      observer?.disconnect()
    }
  }, [doc])

  if (failed || (loaded && doc === null)) return <InboxEmailBody text={fallbackText} />
  if (!doc) {
    return <p data-testid="email-html-loading" className="text-xs text-muted-foreground italic">{HTML_LOADING}</p>
  }
  return (
    <>
      <div className="rounded-md bg-white overflow-hidden">
        <iframe
          ref={frameRef}
          id={frameId}
          data-testid="email-html-frame"
          title={FRAME_TITLE}
          sandbox={SANDBOX}
          srcDoc={doc}
          referrerPolicy="no-referrer"
          style={{ width: '100%', border: 0, height, display: 'block' }}
        />
      </div>
      {folded !== null && (
        <div className="mt-1.5">
          <QuotedToggle open={open} onToggle={() => setOpen((v) => !v)} controls={frameId} />
        </div>
      )}
    </>
  )
}
